import { useEffect } from "react";

let lockCount = 0;
let savedScrollY = 0;
let savedPaddingRight = "";
/** @type {Array<{ el: HTMLElement, overflow: string, scrollTop: number }>} */
let lockedScrollables = [];
/** @type {string | null} */
let activeNestedScrollAllowSelector = null;
/** Selectors from every open overlay. A drawer opened over Bulk Analyse must scroll too. */
/** @type {string[]} */
let allowSelectors = [];

const SCROLLABLE_SELECTORS = [
  ".timesheet-panel-scroll",
  ".custom-scroll",
  "[data-timesheet-scroll]",
].join(",");

const DEFAULT_SCROLL_LOCK_ALLOW_SELECTOR = "[data-scroll-lock-allow]";

/**
 * @param {{ nestedScrollAllowSelector?: string }} [options]
 */
const resolveScrollLockAllowSelector = (options = {}) =>
  options.nestedScrollAllowSelector ?? DEFAULT_SCROLL_LOCK_ALLOW_SELECTOR;

const isInsideScrollLockAllow = (target) => {
  if (!(target instanceof Node)) return false;
  const selectors =
    allowSelectors.length > 0
      ? allowSelectors
      : [resolveScrollLockAllowSelector({
          nestedScrollAllowSelector: activeNestedScrollAllowSelector,
        })];

  return selectors.some((selector) => {
    if (!selector) return false;
    return Array.from(document.querySelectorAll(selector)).some((node) =>
      node.contains(target)
    );
  });
};

const preventBackgroundScroll = (event) => {
  if (isInsideScrollLockAllow(event.target)) return;
  event.preventDefault();
};

const lockNestedScrollContainers = (nestedScrollAllowSelector) => {
  lockedScrollables = [];
  document.querySelectorAll(SCROLLABLE_SELECTORS).forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (
      nestedScrollAllowSelector &&
      node.closest(nestedScrollAllowSelector)
    ) {
      return;
    }
    lockedScrollables.push({
      el: node,
      overflow: node.style.overflow,
      scrollTop: node.scrollTop,
    });
    node.style.overflow = "hidden";
  });
};

const unlockNestedScrollContainers = () => {
  lockedScrollables.forEach(({ el, overflow, scrollTop }) => {
    el.style.overflow = overflow;
    el.scrollTop = scrollTop;
  });
  lockedScrollables = [];
};

const clearInertOnAppShell = () => {
  const root = document.getElementById("root");
  if (root) {
    root.inert = false;
  }
  document.querySelectorAll("body > *").forEach((node) => {
    if (node instanceof HTMLElement) {
      node.inert = false;
    }
  });
};

/**
 * @param {{ nestedScrollAllowSelector?: string }} [options]
 */
const applyLock = (options = {}) => {
  activeNestedScrollAllowSelector =
    options.nestedScrollAllowSelector ?? null;

  savedScrollY = window.scrollY || window.pageYOffset || 0;
  savedPaddingRight = document.body.style.paddingRight;

  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  document.body.style.position = "fixed";
  document.body.style.top = `-${savedScrollY}px`;
  document.body.style.left = "0";
  document.body.style.right = "0";
  document.body.style.width = "100%";
  document.body.style.overflow = "hidden";
  document.documentElement.style.overflow = "hidden";
  document.documentElement.classList.add("v360-scroll-lock");

  lockNestedScrollContainers(activeNestedScrollAllowSelector);

  document.addEventListener("wheel", preventBackgroundScroll, { passive: false });
  document.addEventListener("touchmove", preventBackgroundScroll, { passive: false });
};

const clearLock = () => {
  document.removeEventListener("wheel", preventBackgroundScroll);
  document.removeEventListener("touchmove", preventBackgroundScroll);

  unlockNestedScrollContainers();

  document.documentElement.classList.remove("v360-scroll-lock");
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";
  document.body.style.overflow = "";
  document.documentElement.style.overflow = "";
  document.body.style.paddingRight = savedPaddingRight;
  document.documentElement.style.paddingRight = "";

  window.scrollTo(0, savedScrollY);

  activeNestedScrollAllowSelector = null;
  allowSelectors = [];
  clearInertOnAppShell();
};

/**
 * Acquire a scroll lock; returns release function. Safe for stacked overlays.
 * @param {{ nestedScrollAllowSelector?: string }} [options]
 */
export const acquireBodyScrollLock = (options = {}) => {
  lockCount += 1;
  const selector = options.nestedScrollAllowSelector ?? null;
  if (selector) allowSelectors.push(selector);
  if (lockCount === 1) {
    applyLock(options);
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (selector) {
      const index = allowSelectors.lastIndexOf(selector);
      if (index !== -1) allowSelectors.splice(index, 1);
    }
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      clearLock();
    }
  };
};

/** Clear Headless UI inert + overflow when no overlay still holds a lock. */
export const ensureBodyScrollUnlocked = () => {
  clearInertOnAppShell();
  if (lockCount === 0) {
    clearLock();
  }
};

/** Force-clear all scroll locks (explicit modal dismiss). */
export const releaseBodyScrollLock = () => {
  lockCount = 0;
  clearLock();
};

/**
 * Release after Headless UI Dialog leave transitions.
 * Dialog cleanup can restore a stale overflow:hidden saved while another overlay was open.
 */
export const releaseBodyScrollLockAfterTransition = () => {
  releaseBodyScrollLock();
  window.setTimeout(releaseBodyScrollLock, 0);
  window.setTimeout(releaseBodyScrollLock, 200);
  window.setTimeout(releaseBodyScrollLock, 400);
};

/**
 * Hook for modal/drawer open state.
 * @param {boolean} locked
 * @param {{ nestedScrollAllowSelector?: string }} [options] — e.g. `[data-scroll-lock-allow]` for drawer inner scroll
 */
export const useBodyScrollLock = (locked, options) => {
  const nestedScrollAllowSelector = options?.nestedScrollAllowSelector ?? null;

  useEffect(() => {
    if (!locked) return undefined;
    return acquireBodyScrollLock({ nestedScrollAllowSelector });
  }, [locked, nestedScrollAllowSelector]);
};
